/* eslint-disable max-lines-per-function */
import { ReactNode } from 'react';

import {
   ButtonVariant,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   ShowModalEventInterceptor,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import {
   PrintsForm,
   PrintVisitDocumentsActionEventInterceptor,
} from '~libs.desktop.shared.visit-medical-documents.print-from-visit';

import { visitActionBarTranslationKeys, visitDetailsTranslationKeys } from '../../../../../translations';
import { useVisitPushBack } from '../../../visitForm.hooks';
import { VisitFormController } from '../../../visitForm.types';

import { VisitSummaryConfirmActionEventInterceptor } from './VisitSummaryConfirmActionEventInterceptor';
import { VisitSummaryConfirmAndShareShowModalEventInterceptor } from './VisitSummaryConfirmAndShareShowModalEventInterceptor';

type PrintsShowModalEventInterceptorProps = Pick<VisitFormController, 'handleSubmit'> &
   Readonly<{
      children: ReactNode;
      visitId: number;
      patientId: number;
   }>;

export const PrintsShowModalEventInterceptor = (props: PrintsShowModalEventInterceptorProps): JSX.Element => {
   const pushBack = useVisitPushBack();

   return (
      <ShowModalEventInterceptor
         testId={'confirmVisitAndPrint'}
         title={visitDetailsTranslationKeys.printFromVisit}
         // eslint-disable-next-line
         content={({ resolve, reject }): JSX.Element => (
            <PrintsForm>
               {({ trigger, prints }) => (
                  <>
                     <ValidateFormEventInterceptor trigger={trigger}>
                        <VisitSummaryConfirmActionEventInterceptor
                           handleSubmit={props.handleSubmit}
                           visitId={props.visitId}
                        >
                           <PrintVisitDocumentsActionEventInterceptor prints={prints} visitId={props.visitId}>
                              <ModalConfirmButton
                                 label={visitActionBarTranslationKeys.confirmAndPrint}
                                 onClick={() => {
                                    resolve();
                                    pushBack();
                                 }}
                              />
                           </PrintVisitDocumentsActionEventInterceptor>
                        </VisitSummaryConfirmActionEventInterceptor>
                     </ValidateFormEventInterceptor>

                     {protectFeature(
                        'sendingDocumentsToPatient',
                        <ValidateFormEventInterceptor trigger={trigger}>
                           <VisitSummaryConfirmAndShareShowModalEventInterceptor
                              patientId={props.patientId}
                              handleSubmit={props.handleSubmit}
                              visitId={props.visitId}
                              prints={prints}
                           >
                              <ModalConfirmButton
                                 testId={'confirmAndShareMedicalDocumentation'}
                                 label={visitActionBarTranslationKeys.confirmAndShare}
                                 variant={ButtonVariant.SECONDARY}
                                 onClick={pushBack}
                                 disabled={prints.otherDocuments}
                                 tooltipContent={
                                    prints.otherDocuments
                                       ? visitActionBarTranslationKeys.confirmAndShareTooltip
                                       : undefined
                                 }
                              />
                           </VisitSummaryConfirmAndShareShowModalEventInterceptor>
                        </ValidateFormEventInterceptor>,
                        null
                     )}

                     <ModalCancelButton onClick={reject} />
                     <ModalCloseButton onClick={reject} />
                  </>
               )}
            </PrintsForm>
         )}
         modalWidth={ModalWidth.NORMAL}
         shouldShowModal
         footerItemsHidden
      >
         {props.children}
      </ShowModalEventInterceptor>
   );
};
